/**
 * Tournament content container and tab management.
 * Creates and manages tournament tab structure and visibility.
 */
import { removeAllChildNodes } from 'services/dom/transformers';

import {
  EVENTS_CONTROL,
  MATCHUPS_CONTROL,
  PARTICIPANT_CONTROL,
  SCHEDULE_CONTROL,
  TOURNAMENT_EVENTS,
  VENUES_CONTROL,
  ACCEPTED_PANEL,
  ALTERNATES_PANEL,
  QUALIFYING_PANEL,
  UNGROUPED_PANEL,
  WITHDRAWN_PANEL,
  DRAWS_VIEW,
  POINTS_VIEW,
  EVENT_CONTROL,
  TOURNAMENT_CONTAINER,
  TOURNAMENT_MATCHUPS,
  TOURNAMENT_SCHEDULE,
  TEAMS_CONTROL,
  TOURNAMENT_PARTICIPANTS,
  TOURNAMENT_TEAMS,
  PARTICIPANTS,
  MATCHUPS_TAB,
  PUBLISHING_TAB,
  PUBLISHING_CONTROL,
  TOURNAMENT_PUBLISHING,
  SCHEDULE_TAB,
  SCHEDULE2_TAB,
  SCHEDULE2_CONTAINER,
  SCHEDULE2_CONTROL,
  UNSCHEDULED_MATCHUPS,
  UNSCHEDULED_CONTROL,
  UNSCHEDULED_VISIBILITY,
  VENUES_TAB,
  TEMPORAL_GRID_CONTAINER,
  TOURNAMENT_VENUES,
  NONE,
  SUCCESS,
  EVENTS_TAB,
  TOURNAMENT_OVERVIEW,
  TEAM_STATS,
  SETTINGS_TAB,
  SETTINGS_CONTROL,
  TOURNAMENT_SETTINGS,
} from 'constants/tmxConstants';

const refMap: Record<string, string> = {
  [TOURNAMENT_OVERVIEW]: 'o-tab',
  [PARTICIPANTS]: 'p-tab',
  [EVENTS_TAB]: 'e-tab',
  [MATCHUPS_TAB]: 'm-tab',
  [SCHEDULE_TAB]: 's-tab',
  [SCHEDULE2_TAB]: 's2-tab',
  [VENUES_TAB]: 'v-tab',
  [PUBLISHING_TAB]: 'b-tab',
  [SETTINGS_TAB]: 'c-tab',
};

export function tournamentContent(): void {
  const participantsTab = `
        <div id='individuals' class='tab_section participants_tab'>
          <div class='section'>
            <div class='tabHeader foreground'>Participants</div>
            <div id='${PARTICIPANT_CONTROL}' class='controlBar'></div>
            <div id='${TOURNAMENT_PARTICIPANTS}' class='tableClass flexcol flexcenter'> </div>
          </div>
        </div>
        <div id='participantGroupings' class='tab_section participants_tab'>
            <div class='section'>
              <div class='tabHeader foreground'></div>
              <div id='${TEAMS_CONTROL}' class='controlBar'></div>
              <div id='${TOURNAMENT_TEAMS}' class='tableClass flexcol flexcenter'> </div>
            </div>
        </div>
        `;

  const scheduleTab = `
        <div class='tab_section sch_tab'>
            <div class='section block'>
              <div id='${UNSCHEDULED_VISIBILITY}' style='width: 100%; display: none;'>
                  <div id='${UNSCHEDULED_CONTROL}' class='controlBar flexcol flexcenter'></div>
                  <div id='${UNSCHEDULED_MATCHUPS}' class='tableClass flexcol flexcenter'></div>
              </div>
              <div>
                  <div id='${SCHEDULE_CONTROL}' class='controlBar flexcol flexcenter'></div>
                  <div id='${TOURNAMENT_SCHEDULE}' class='tableClass flexcol flexcenter'></div>
              </div>
            </div>
        </div>
        `;

  const schedule2Tab = `
        <div class='tab_section sch2_tab'>
            <div class='section block' style='min-height: auto;'>
              <div id='${SCHEDULE2_CONTROL}' class='controlBar flexcol flexcenter'></div>
              <div id='${SCHEDULE2_CONTAINER}' style='width: 100%; height: calc(100vh - 140px);'></div>
            </div>
        </div>
        `;

  const venuesTab = `
        <div class='tab_section venues_tab'>
            <div class='section'>
              <div class='tabHeader foreground'></div>
              <div id='${VENUES_CONTROL}' class='controlBar'></div>
              <div id='${TOURNAMENT_VENUES}' class='tableClass flexcol flexcenter'></div>
              <div id='${TEMPORAL_GRID_CONTAINER}' style='display: none; width: 100%; height: 75vh; overflow: hidden;'></div>
            </div>
        </div>
        `;

  const matchUpsTab = `
        <div class='tab_section matchUps_tab'>
            <div class='section'>
              <div class='tabHeader foreground'></div>
              <div id='${MATCHUPS_CONTROL}' class='controlBar'></div>
              <div id='${TEAM_STATS}' class='controlBar'></div>
              <div id='${TOURNAMENT_MATCHUPS}' class='tableClass flexcol flexcenter'> </div>
            </div>
        </div>
        `;

  const settingsTab = `
        <div class='tab_section settings_tab'>
            <div class='section' style='min-height: auto;'>
              <div id='${SETTINGS_CONTROL}' class='controlBar'></div>
              <div id='${TOURNAMENT_SETTINGS}'></div>
            </div>
        </div>
        `;

  const publishingTab = `
        <div class='tab_section publishing_tab'>
            <div class='section' style='min-height: auto;'>
              <div id='${PUBLISHING_CONTROL}' class='controlBar'></div>
              <div id='${TOURNAMENT_PUBLISHING}'></div>
            </div>
        </div>
        `;

  const tabs: Record<string, string> = {
    'p-tab': participantsTab,
    's-tab': scheduleTab,
    's2-tab': schedule2Tab,
    'v-tab': venuesTab,
    'm-tab': matchUpsTab,
    'b-tab': publishingTab,
    'c-tab': settingsTab,
  };

  const contentContainer = document.getElementById(TOURNAMENT_CONTAINER)!;

  Object.keys(tabs).forEach((id) => {
    const elem = document.createElement('div');
    elem.className = 'is-marginless';
    elem.style.cssText = 'width: inherit';
    elem.id = id;
    elem.innerHTML = tabs[id];
    contentContainer.appendChild(elem);
  });

  displayTab(PARTICIPANTS);
}

export function displayTab(reference: string): any {
  if (!Object.keys(refMap).includes(reference)) return false;

  Object.keys(refMap).forEach((key) => {
    const id = refMap[key];
    const element = document.getElementById(id);
    if (element) {
      element.style.display = reference === key ? '' : NONE;
    } else {
      console.log({ key, id, element });
    }
  });

  return { ...SUCCESS };
}

export function removeTournamentContent(): void {
  const ids = [
    DRAWS_VIEW,
    POINTS_VIEW,
    EVENTS_CONTROL,
    MATCHUPS_CONTROL,
    PARTICIPANT_CONTROL,
    UNSCHEDULED_CONTROL,
    EVENT_CONTROL,
    TEAMS_CONTROL,
    SCHEDULE_CONTROL,
    SCHEDULE2_CONTROL,
    VENUES_CONTROL,

    TEAM_STATS,

    ACCEPTED_PANEL,
    ALTERNATES_PANEL,
    QUALIFYING_PANEL,
    UNGROUPED_PANEL,
    WITHDRAWN_PANEL,

    TOURNAMENT_EVENTS,
    TOURNAMENT_MATCHUPS,
    TOURNAMENT_PARTICIPANTS,
    TOURNAMENT_SCHEDULE,
    SCHEDULE2_CONTAINER,
    TOURNAMENT_TEAMS,
    UNSCHEDULED_MATCHUPS,
    TOURNAMENT_VENUES,
    TEMPORAL_GRID_CONTAINER,
    TOURNAMENT_PUBLISHING,
  ];

  ids.forEach((key) => {
    const id = refMap[key];
    const element = document.getElementById(id);
    if (element) removeAllChildNodes(element);
  });
}
